import Card from "components/card";
import { Rating } from "@aws-amplify/ui-react";
import { useDealData } from "contexts/DealContext";
import SolidSubtleMultiAlert from "components/alert/SolidSubtleMultiAlert";
import SolidSubtleAlert from "components/alert/SolidSubtleAlert";
import MissingCCNAlert from "components/alert/MissingCCNAlert";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { TbBuildingCommunity } from "react-icons/tb";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useState, useRef, useEffect } from "react";
import { useTabs } from "contexts/TabContext";
import { IoMdClose } from "react-icons/io";
import { IoOpenOutline } from "react-icons/io5";

const DealTitle = () => {
  const {
    currentDealName,
    currentDeal,
    currentFacility,
    currentGroup,
    competitionData
  } = useDealData();

  const { addTab } = useTabs();

  // State for showing/hiding facilities list
  const [showFacilities, setShowFacilities] = useState(false);
  const [isDealInfoPopupOpen, setIsDealInfoPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const dealInfoButtonRef = useRef(null);
  const popupRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target) &&
        dealInfoButtonRef.current && !dealInfoButtonRef.current.contains(event.target)) {
        setIsDealInfoPopupOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to handle competition icon click and create tab
  const handleCompetitionClick = () => {
    // Create the path including the deal name if available
    let path;
    if (currentDealName) {
      path = `/deals/${encodeURIComponent(currentDealName)}/competition-charts/competition-analysis`;
    } else {
      path = `/competition-charts/competition-analysis`;
    }

    // Add the tab using the context
    addTab('competition-analysis', 'Competition Analysis', path);
  };

  // If no deal is selected, display nothing
  if (!currentDeal) return null;

  // Get deal info if available
  const dealInfo = currentDeal?.facilities?.["Consolidated"]?.details?.["Deal Info"];
  const hasDealInfo = dealInfo && dealInfo.length > 0;

  // Calculate some basic stats for display
  const facilities = currentDeal?.facilities || {};
  const numberOfFacilities = Object.keys(facilities).filter(name =>
    name.toLowerCase() !== 'consolidated'
  ).length;

  // Get consolidated facility for overall ratings and stats
  const consolidatedFacility = facilities?.Consolidated || facilities?.consolidated;
  const consolidatedRating = Number(consolidatedFacility?.details?.["Star Rating"] || 0);

  // Determine if we're viewing a specific facility vs consolidated view
  const isSpecificFacilitySelected = currentFacility &&
    Object.entries(facilities).find(([name, facility]) =>
      facility === currentFacility && name.toLowerCase() !== 'consolidated'
    );

  const isConsolidatedView = currentFacility === consolidatedFacility;

  // Check if current selection is a group
  const isGroup = currentFacility?.details?.isGroup;

  // Get the current group details if it's a group
  const groupFacilityNames = currentGroup?.facilityIds?.map(id => {
    const facilityEntry = Object.entries(facilities).find(([, facility]) =>
      facility.details?.id === id || facility.details?.CCN === id
    );
    return facilityEntry ? facilityEntry[1].details?.Facilities : null;
  }).filter(Boolean) || [];

  // Count SFF facilities for alerts
  const sffCount = Object.values(facilities).filter(facility =>
    facility?.details?.["SFF"] &&
    facility !== consolidatedFacility
  ).length;

  // Facilities with no SNF beds are AL facilities and don't have a CCN — skip them
  const hasSnfBeds = (facility) =>
    (parseInt(facility?.details?.["SNF Beds"]) || 0) > 0;

  // Determine facilities missing a CCN (for consolidated view alert)
  const facilitiesMissingCCN = Object.entries(facilities)
    .filter(([name, facility]) =>
      name.toLowerCase() !== 'consolidated' &&
      !facility?.details?.isGroup &&
      hasSnfBeds(facility) &&
      !facility?.details?.["CCN"]
    )
    .map(([, facility]) => facility?.details?.Facilities || facility?.details?.["Facility Name"] || "Unknown");

  const currentFacilityMissingCCN =
    isSpecificFacilitySelected &&
    hasSnfBeds(currentFacility) &&
    !currentFacility?.details?.["CCN"];

  // Get facility ID for competition data lookup
  let facilityId = null;
  if (isSpecificFacilitySelected) {
    const facilityDetails = currentFacility.details || {};
    facilityId = facilityDetails.CCN ||
      facilityDetails.ccn ||
      facilityDetails.ProviderNumber ||
      Object.keys(competitionData[currentDealName] || {})[0];
  }

  // Toggle deal info popup
  const toggleDealInfoPopup = (e) => {
    if (e) {
      e.stopPropagation();

      // Calculate position based on button location
      if (dealInfoButtonRef.current) {
        const rect = dealInfoButtonRef.current.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX - 150, // Center the popup
        });
      }
    }

    setIsDealInfoPopupOpen(!isDealInfoPopupOpen);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {(currentFacilityMissingCCN || (!isSpecificFacilitySelected && !isGroup && facilitiesMissingCCN.length > 0)) && (
        <MissingCCNAlert
          isSpecificFacility={!!currentFacilityMissingCCN}
          missingFacilities={facilitiesMissingCCN}
          resetKey={`${currentDealName}|${currentFacility?.details?.Facilities || ''}`}
        />
      )}
      <Card extra={"sm:!flex-row gap-2 w-full h-full p-[16px] bg-cover sm:divide-x"}>
      <div className="sm:basis-[40%] sm:m-2 flex flex-col items-center w-full justify-center rounded-xl border-l-1">
        <h2 className="mb-2 text-4xl text-center font-bold text-navy-700 dark:text-white">
          {currentDealName}
        </h2>
        {(currentDeal?.lastUpdated || consolidatedFacility?.details?.["Last Updated"] || currentFacility?.details?.["Last Updated"]) && (
          <p className="text-xs text-gray-500 mb-2">
            Last Updated: {currentDeal?.lastUpdated || consolidatedFacility?.details?.["Last Updated"] || currentFacility?.details?.["Last Updated"]}
          </p>
        )}

        {/* Consolidated Title */}
        {!isSpecificFacilitySelected && !isGroup && (
          <div className="flex items-center">
            <p className="text-xl text-gray-800 dark:text-gray-400">
              {currentFacility?.details?.State}
            </p>
            {hasDealInfo && (
              <button
                ref={dealInfoButtonRef}
                onClick={toggleDealInfoPopup}
                className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors relative"
                title="View deal information"
              >
                <AiOutlineInfoCircle className="h-5 w-5 text-blue-600" />
              </button>
            )}
            <button
              onClick={handleCompetitionClick}
              className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="View competition data"
            >
              <TbBuildingCommunity className="h-5 w-5 text-[#755263]" />
            </button>
          </div>
        )}

        {/* Group Title in left column - simple version */}
        {isGroup && (
          <p className="text-xl text-gray-800 dark:text-gray-400">
            {currentFacility?.details?.Facilities}
          </p>
        )}

        {/* Single Facility Title */}
        {isSpecificFacilitySelected && (
          <div className="flex items-center">
            <div className="flex items-center">
              <p className="text-xl text-gray-800 dark:text-gray-400">
                {currentFacility?.details?.Facilities}
              </p>
              {currentFacility?.details?.["City, State"] && (
                <>
                  <span className="mx-2 text-gray-600 dark:text-gray-400">|</span>
                  <p className="text-md capitalize text-gray-600 dark:text-gray-400">
                    {currentFacility?.details?.["City, State"]}
                  </p>
                </>
              )}
            </div>

            {hasDealInfo && (
              <button
                ref={dealInfoButtonRef}
                onClick={toggleDealInfoPopup}
                className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors relative"
                title="View deal information"
              >
                <AiOutlineInfoCircle className="h-5 w-5 text-blue-600" />
              </button>
            )}
            <button
              onClick={handleCompetitionClick}
              className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="View competition data"
            >
              <TbBuildingCommunity className="h-5 w-5 text-[#755263]" />
            </button>
          </div>
        )}

        <div className="flex divide-x w-full justify-center">
          {currentFacility?.details?.["Years Built"] && (
            <p className="px-2 text-right font-normal text-gray-800">
              {"Built: " + (
                isNaN(Math.floor(currentFacility?.details["Years Built"]))
                  ? currentFacility.details["Years Built"]
                  : Math.floor(currentFacility?.details["Years Built"])
              )}
            </p>
          )}
          {currentFacility?.details?.["Years Renovated"] && (
            <p className="px-2 text-left font-normal text-gray-800">
              {"Renovated: " + (
                isNaN(Math.floor(currentFacility?.details["Years Renovated"]))
                  ? currentFacility?.details["Years Renovated"]
                  : Math.floor(currentFacility?.details["Years Renovated"])
              )}
            </p>
          )}
        </div>
        {currentFacility?.details?.["CCN"] && (
          <a
            href={`https://www.medicare.gov/care-compare/details/nursing-home/${currentFacility?.details?.["CCN"]}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-blue-600 text-left font-normal flex items-center gap-1 hover:underline transition-colors"
          >
            View at Medicare.gov
            <IoOpenOutline className="ml-1" />
          </a>
        )}
      </div>

      <div className="flex flex-row basis-[60%] justify-around divide-x w-full h-full gap-0">
        <div className="px-2 flex flex-col basis-1/3 items-center justify-center">
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
            {numberOfFacilities}
          </h4>
          <p className="text-center font-normal text-gray-600">Buildings in this deal</p>

          {isGroup && (
            <div className="mt-3 mx-3 p-4 bg-blue-50 rounded-lg text-sm w-full">
              <div className="flex flex-col items-center">
                <p className="text-sm font-medium text-blue-800">
                  Group: {currentGroup?.facilityIds?.length || 0} facilities
                </p>

                {/* Collapsible facilities list */}
                {groupFacilityNames.length > 0 && (
                  <div className="mt-1 w-full">
                    <button
                      onClick={() => setShowFacilities(!showFacilities)}
                      className="flex items-center justify-center w-full text-xs font-medium text-blue-700 hover:text-blue-800 transition-colors"
                    >
                      {showFacilities ? "Hide facilities" : "Show facilities"}
                      {showFacilities ?
                        <HiChevronUp className="ml-1" /> :
                        <HiChevronDown className="ml-1" />
                      }
                    </button>

                    {showFacilities && (
                      <div className="mt-2 p-2 bg-white rounded border border-blue-100 text-xs">
                        <ul className="list-disc pl-4 space-y-1">
                          {groupFacilityNames.map((name, index) => (
                            <li key={index} className="text-gray-600">{name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-2 flex flex-col gap-2 basis-1/3 items-center justify-center">
          <div className="px-2 flex flex-col basis-2/3 items-center justify-center">
            <Rating value={consolidatedRating} fillColor="#f0b41c" />
            <p className="mt-1 font-normal text-scenter text-gray-600">Deal Overall Star Rating</p>
          </div>

          {/* SFF indicators */}
          {!isSpecificFacilitySelected && sffCount > 0 && (
            <div className="px-4 pt-2 w-full items-center justify-center">
              <SolidSubtleAlert
                title={sffCount}
                description="SFF facilities in this deal"
                icon={<IoIosInformationCircleOutline />}
                iconColor="text-navy-700"
                bg="bg-[#fee2e2] dark:!bg-navy-700"
                closeBg="hover:bg-white/20 text-navy-900 dark:text-white"
              />
            </div>
          )}

          {isSpecificFacilitySelected && currentFacility?.details?.["SFF"] && (
            <div className="px-4 pt-2 w-full items-center justify-center">
              <SolidSubtleMultiAlert
                title="SFF STATUS"
                description={currentFacility.details["SFF"]}
                icon={<IoIosInformationCircleOutline />}
                iconColor="text-navy-700"
                bg="bg-[#fee2e2] dark:!bg-navy-700"
                closeBg="hover:bg-white/20 text-navy-900 dark:text-white"
              />
            </div>
          )}
        </div>

        <div className="p-2 flex flex-col gap-2 basis-1/3 items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {consolidatedFacility?.details?.["Total Beds"]}
            </h4>
            <p className="text-center font-normal text-gray-600">Total Beds</p>
          </div>

          {/* Show group or specific facility beds */}
          {(isSpecificFacilitySelected || isGroup) && (
            <div className="flex flex-col items-center justify-center">
              <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                {currentFacility?.details?.["Total Beds"]}
              </h4>
              <p className="text-center text-sm text-gray-600">
                {isGroup ? "Group Beds" : "Facility Beds"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Deal Info Popup */}
      {isDealInfoPopupOpen && hasDealInfo && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white dark:bg-navy-800 rounded-lg shadow-lg border border-gray-200 dark:border-navy-700 w-80"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-navy-700">
            <h3 className="font-semibold text-navy-700 dark:text-white">Deal Information</h3>
            <button
              onClick={toggleDealInfoPopup}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
            >
              <IoMdClose className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            {Array.isArray(dealInfo) ? (
              <div className="space-y-1">
                {dealInfo.map((item, index) => (
                  <p key={index} className="text-sm text-gray-800 dark:text-gray-200">{item}</p>
                ))}
              </div>
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{dealInfo}</pre>
            )}
          </div>
        </div>
      )}
    </Card>
    </div>
  );
};

export default DealTitle;